import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Iter-311c — Public SMS Consent Proof Page (Twilio A2P 10DLC).
 *
 * Route: /sms-consent (public, no auth)
 *
 * Purpose: This page is submitted to Twilio during A2P 10DLC campaign
 * registration as the documentation of our opt-in workflow. It must
 * illustrate:
 *   1. The registration form asks for a phone number.
 *   2. A SEPARATE, UNCHECKED SMS consent checkbox appears.
 *   3. The SMS disclosure text (identical to the checkbox label).
 *   4. Links to /privacy-policy and /terms-and-conditions.
 *
 * We render the exact same disclosure text used at signup so Twilio
 * reviewers can verify the language matches the checkbox.
 */
const SmsConsentPage = () => {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200 px-4 py-12">
      <div className="max-w-3xl mx-auto space-y-8" data-testid="sms-consent-page">
        <div>
          <h1 className="text-3xl font-bold text-white">iEvntür SMS Consent &amp; Opt-In Workflow</h1>
          <p className="text-zinc-400 text-sm mt-2">
            This page documents the SMS opt-in flow used by the iEvntür
            platform (<code>app.ievntur.com</code>). It is provided as
            proof of consent for A2P 10DLC campaign registration with Twilio.
          </p>
          <p className="text-zinc-500 text-xs mt-1">Effective 2026-07-01 · v1</p>
        </div>

        {/* ─── Step 1 — Phone field ─── */}
        <section
          className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 space-y-3"
          data-testid="sms-consent-step-phone"
        >
          <h2 className="text-lg font-semibold text-white">Step 1 — User provides a phone number</h2>
          <p className="text-sm text-zinc-300">
            During registration on <a className="text-amber-400 underline" href="https://app.ievntur.com/register">app.ievntur.com/register</a>,
            the user enters their mobile number in the Phone field. This field is optional for
            account creation.
          </p>
          <div className="rounded border border-zinc-700 bg-zinc-950 p-4 max-w-md">
            <label className="block text-xs text-zinc-400 mb-1">Phone number</label>
            <div className="h-10 rounded bg-zinc-800/50 border border-zinc-700 flex items-center px-3 text-sm text-zinc-500">
              (555) 555-1234
            </div>
          </div>
        </section>

        {/* ─── Step 2 — Unchecked SMS consent checkbox ─── */}
        <section
          className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 space-y-3"
          data-testid="sms-consent-step-checkbox"
        >
          <h2 className="text-lg font-semibold text-white">Step 2 — Separate, unchecked SMS consent checkbox</h2>
          <p className="text-sm text-zinc-300">
            When a phone number is entered, a <strong>separate</strong> SMS
            consent checkbox appears below the phone field. It is{' '}
            <strong className="text-amber-400">UNCHECKED by default</strong> and is{' '}
            <strong>not required</strong> to create an account. Users who leave
            it unchecked will not receive any promotional SMS messages.
          </p>

          {/* Live mirror of the registration UI */}
          <div className="rounded-lg border border-amber-700/40 bg-amber-900/10 p-3 space-y-2">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={false}
                readOnly
                className="mt-1 w-4 h-4 rounded border-zinc-500 bg-zinc-800 text-amber-500 focus:ring-amber-500"
                aria-label="SMS consent — unchecked example"
                data-testid="sms-consent-proof-checkbox"
              />
              <span className="text-sm text-zinc-200 leading-snug" data-testid="sms-consent-proof-copy">
                I agree to receive SMS messages from iEvntür regarding booking
                requests, event opportunities, scheduling updates, account
                notifications, verification codes, and customer support. Message
                frequency varies. Message and data rates may apply. Reply{' '}
                <strong>STOP</strong> to opt out and <strong>HELP</strong> for
                assistance. See our{' '}
                <Link to="/privacy-policy" className="text-amber-400 underline">Privacy Policy</Link>{' '}
                and{' '}
                <Link to="/terms-and-conditions" className="text-amber-400 underline">Terms &amp; Conditions</Link>.
              </span>
            </div>
          </div>

          <p className="text-xs text-zinc-500 italic">
            The above is the exact checkbox rendered on the live registration
            page. Reviewers can verify this by visiting the /register route.
          </p>
        </section>

        {/* ─── Step 3 — Consent record ─── */}
        <section
          className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 space-y-3"
          data-testid="sms-consent-step-record"
        >
          <h2 className="text-lg font-semibold text-white">Step 3 — Consent captured on server</h2>
          <p className="text-sm text-zinc-300">
            When the user checks the box and submits the form, the backend
            calls <code className="text-amber-400">POST /api/sms/consent</code> which
            persists the consent record including:
          </p>
          <ul className="list-disc pl-6 text-sm text-zinc-300 space-y-1">
            <li>User ID, email, and phone number</li>
            <li>Consent timestamp (UTC)</li>
            <li>IP address at time of consent</li>
            <li>User-agent string</li>
            <li>Consent version (currently <code>v1</code>)</li>
            <li>Verbatim disclosure text shown to the user</li>
          </ul>
          <p className="text-xs text-zinc-500 italic">
            Records are retained indefinitely to support opt-in audits.
          </p>
        </section>

        {/* ─── Program details ─── */}
        <section
          className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 space-y-3"
          data-testid="sms-consent-program-details"
        >
          <h2 className="text-lg font-semibold text-white">Program Details</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-zinc-500 text-xs uppercase tracking-wide">Program name</dt>
              <dd className="text-zinc-100 font-medium">iEvntür SMS Notifications</dd>
            </div>
            <div>
              <dt className="text-zinc-500 text-xs uppercase tracking-wide">Brand</dt>
              <dd className="text-zinc-100 font-medium">iEvntür</dd>
            </div>
            <div>
              <dt className="text-zinc-500 text-xs uppercase tracking-wide">Website</dt>
              <dd><a href="https://app.ievntur.com" className="text-amber-400 underline">https://app.ievntur.com</a></dd>
            </div>
            <div>
              <dt className="text-zinc-500 text-xs uppercase tracking-wide">Support email</dt>
              <dd><a href="mailto:support@ievntur.com" className="text-amber-400 underline">support@ievntur.com</a></dd>
            </div>
            <div>
              <dt className="text-zinc-500 text-xs uppercase tracking-wide">Opt-in method</dt>
              <dd className="text-zinc-100">Explicit checkbox on web signup (unchecked by default)</dd>
            </div>
            <div>
              <dt className="text-zinc-500 text-xs uppercase tracking-wide">Opt-out method</dt>
              <dd className="text-zinc-100">Reply STOP to any message</dd>
            </div>
            <div>
              <dt className="text-zinc-500 text-xs uppercase tracking-wide">Help keyword</dt>
              <dd className="text-zinc-100">Reply HELP for support info</dd>
            </div>
            <div>
              <dt className="text-zinc-500 text-xs uppercase tracking-wide">Message frequency</dt>
              <dd className="text-zinc-100">Varies (typically 1–10 msgs per booking)</dd>
            </div>
          </dl>
        </section>

        {/* ─── Sample messages ─── */}
        <section
          className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 space-y-3"
          data-testid="sms-consent-sample-messages"
        >
          <h2 className="text-lg font-semibold text-white">Sample messages</h2>
          <div className="space-y-2 text-sm">
            <div className="rounded bg-zinc-950 border border-zinc-700 p-3">
              <p className="text-zinc-500 text-xs mb-1">Verification code</p>
              <p className="text-zinc-100">iEvntür: Your verification code is 482910. Do not share this code. Reply STOP to opt out.</p>
            </div>
            <div className="rounded bg-zinc-950 border border-zinc-700 p-3">
              <p className="text-zinc-500 text-xs mb-1">Booking confirmation</p>
              <p className="text-zinc-100">iEvntür: Your shift at Grand Royale on Aug 15 8:00 PM has been confirmed. See details in the app. Reply STOP to opt out.</p>
            </div>
            <div className="rounded bg-zinc-950 border border-zinc-700 p-3">
              <p className="text-zinc-500 text-xs mb-1">Shift reminder</p>
              <p className="text-zinc-100">iEvntür reminder: Your shift at SweetWater Brewery starts in 2 hours. Check the app for directions. Reply STOP to opt out.</p>
            </div>
            <div className="rounded bg-zinc-950 border border-zinc-700 p-3">
              <p className="text-zinc-500 text-xs mb-1">HELP reply</p>
              <p className="text-zinc-100">iEvntür SMS Notifications. For help contact support@ievntur.com. Msg &amp; data rates may apply. Reply STOP to opt out.</p>
            </div>
            <div className="rounded bg-zinc-950 border border-zinc-700 p-3">
              <p className="text-zinc-500 text-xs mb-1">STOP reply</p>
              <p className="text-zinc-100">You have been unsubscribed from iEvntür SMS messages. No further messages will be sent. Reply START to resubscribe.</p>
            </div>
          </div>
        </section>

        {/* ─── Related documents ─── */}
        <section
          className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 space-y-2"
          data-testid="sms-consent-related-docs"
        >
          <h2 className="text-lg font-semibold text-white">Related documents</h2>
          <ul className="list-disc pl-6 text-sm space-y-1">
            <li><Link to="/privacy-policy" className="text-amber-400 underline">Privacy Policy</Link> — data collection, use, and non-sale statement</li>
            <li><Link to="/terms-and-conditions" className="text-amber-400 underline">Terms &amp; Conditions</Link> — includes full SMS Messaging Terms</li>
            <li><Link to="/sms-terms" className="text-amber-400 underline">SMS Terms &amp; Conditions</Link> — condensed SMS-only terms</li>
            <li><Link to="/register" className="text-amber-400 underline">Live registration page</Link> — see the actual opt-in workflow in production</li>
          </ul>
        </section>

        <p className="text-xs text-zinc-500 border-t border-zinc-800 pt-4">
          Questions or verification requests:{' '}
          <a href="mailto:support@ievntur.com" className="text-amber-400 underline">
            support@ievntur.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default SmsConsentPage;
